import React from "react";
import { VisualProps } from "../visual";
import { QueryVisual } from "lib/visuals/types";
import { useSession } from "../../session/session";
import { useActiveContext } from "shared/ContextSwitcher/store";
import { useGetAvailablePerformanceTiers } from "../../dashboard/useGetAvailablePerformanceTiers";
import {
  currentParametersForQuery,
  useDashboardParameters,
} from "lib/parameters/hooks";
import { getResultQueryParams } from "lib/results/useQueryResultLegacy";
import { executeQueryAndAddToastOnError } from "shared/executeQuery/executeQueryAndAddToastOnError";
import { apolloCore } from "lib/apollo/apollo";
import { resultStatusDone } from "lib/results/status";
import { isDefined } from "lib/types/types";
import * as Sentry from "@sentry/react";
import { Tooltip } from "components/Tooltip/Tooltip";
import styles from "./RefreshQueryButton.module.css";
import { IconArrowCounterClockwise } from "components/Icons/IconArrowCounterClockwise";

const withDashboard = (
  BaseComponent: React.FC<
    VisualProps<QueryVisual> &
      Required<Pick<VisualProps<QueryVisual>, "dashboard">>
  >
) => (props: VisualProps<QueryVisual>) => {
  if (!isDefined(props.dashboard)) {
    return null;
  }

  return <BaseComponent {...props} dashboard={props.dashboard} />;
};

export const RefreshQueryButton = withDashboard((props) => {
  const session = useSession();
  const activeContext = useActiveContext();
  const isResultReady =
    resultStatusDone(props.result) && props.result?.loading === false;

  const getAvailablePerformanceTiers = useGetAvailablePerformanceTiers(
    activeContext
  );

  const datasetId = props.visual.query_details?.dataset_id;
  const performance = getAvailablePerformanceTiers(datasetId).find(
    ({ credits }) => credits > 0
  )?.publicPerformanceTierKey;
  const { queryId } = props;

  const parameters = useDashboardParameters(props.dashboard);

  if (
    !session ||
    !activeContext ||
    !isDefined(queryId) ||
    !isDefined(performance) ||
    !isResultReady
  ) {
    return null;
  }

  const queryParameters = currentParametersForQuery(queryId, parameters);

  const onClick = () => {
    const getResultParams = getResultQueryParams({
      queryId,
      parameters: queryParameters,
      can_refresh: queryParameters.every((p) => p.value === p.default_value),
      apiKey: undefined,
      session,
    });

    // Execute the query
    executeQueryAndAddToastOnError({
      queryId,
      parameters: getResultParams.variables.parameters,
      requestContext: getResultParams.context,
      activeContext,
      performance,
      executionType: "interactive",
    })
      // Call the GetResult graphql query that is called as part of the useQueryResult
      // hook. Since we have just triggered a fresh execution, get a new result for this
      // graphql query should override the cache and trigger the polling workflow to
      // fetch the new result
      .then(() => {
        apolloCore.query({
          query: getResultParams.queryDoc,
          variables: getResultParams.variables,
          context: getResultParams.context,
          fetchPolicy: "network-only",
        });
      })
      .catch((err) => {
        Sentry.captureException(
          Error(
            `Error ${err} while refreshing the query with id ${props.queryId}`
          ),
          {
            extra: {
              type: activeContext?.type,
              id: activeContext?.id,
              performance,
            },
          }
        );
      });
  };

  return (
    <Tooltip label="Refresh results" position="above-center">
      <button className={styles.refreshButton} onClick={onClick}>
        <IconArrowCounterClockwise />
      </button>
    </Tooltip>
  );
});
